import { Info, Loader2, Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';

type ProductionDeclarationMaterialCommodityOriginsBannerProps = {
  disabled?: boolean;
  hasMultipleOrigins: boolean;
  isCreating?: boolean;
  onCreate?: () => void;
};

export function ProductionDeclarationMaterialCommodityOriginsBanner({
  disabled = false,
  hasMultipleOrigins,
  isCreating = false,
  onCreate,
}: ProductionDeclarationMaterialCommodityOriginsBannerProps) {
  const { t } = useTranslation('productionDeclaration', {
    useSuspense: false,
  });

  return (
    <div className='rounded-[1.4rem] border border-declaration-border bg-white px-7 py-6 shadow-[0_10px_24px_rgba(17,24,39,0.06)] sm:px-8'>
      <div className='flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between'>
        <div className='flex items-start gap-2.5'>
          <p className='font-semibold text-[1.05rem] text-declaration-foreground leading-7 tracking-tight'>
            {t(
              hasMultipleOrigins
                ? 'materialCommodityOrigins.banner.multiple'
                : 'materialCommodityOrigins.banner.single',
            )}
          </p>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                aria-label={t('materialCommodityOrigins.banner.tooltip.title')}
                className='mt-1 rounded-sm text-declaration-muted-foreground transition-colors hover:text-declaration-foreground-muted'
                type='button'
              >
                <Info aria-hidden className='size-5 shrink-0' />
              </button>
            </TooltipTrigger>
            <TooltipContent
              align='start'
              className='w-[min(40rem,calc(100vw-2rem))] space-y-3 rounded-xl border border-declaration-border bg-white p-4 text-left text-declaration-foreground text-sm leading-6 shadow-[0_10px_24px_rgba(17,24,39,0.08)] dark:border-declaration-border dark:bg-white dark:text-declaration-foreground'
              collisionPadding={24}
              showArrow={false}
              side='bottom'
              sideOffset={10}
            >
              <p className='font-semibold text-declaration-foreground text-sm'>
                {t('materialCommodityOrigins.banner.tooltip.title')}
              </p>
              <p>{t('materialCommodityOrigins.banner.tooltip.intro')}</p>
              <p>{t('materialCommodityOrigins.banner.tooltip.detailsLead')}</p>
              <ul className='list-disc space-y-1 pl-5'>
                <li>{t('materialCommodityOrigins.banner.tooltip.bullet1')}</li>
                <li>{t('materialCommodityOrigins.banner.tooltip.bullet2')}</li>
                <li>{t('materialCommodityOrigins.banner.tooltip.bullet3')}</li>
              </ul>
            </TooltipContent>
          </Tooltip>
        </div>

        <Button
          aria-busy={isCreating}
          className='h-11 rounded-lg border-declaration-border bg-white px-5 font-medium text-declaration-foreground shadow-none hover:bg-declaration-hover dark:border-declaration-border dark:bg-white dark:text-declaration-foreground dark:hover:bg-declaration-hover'
          disabled={disabled || isCreating}
          onClick={onCreate}
          type='button'
          variant='outline'
        >
          {isCreating ? (
            <Loader2 className='size-5 animate-spin' strokeWidth={2.2} />
          ) : (
            <Plus className='size-5' strokeWidth={2.2} />
          )}
          {t('materialCommodityOrigins.banner.cta')}
        </Button>
      </div>
    </div>
  );
}
